import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { doc, collection, query, where, getDocs, writeBatch, addDoc, updateDoc, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from "./lib/firebase.js";
import { motion, AnimatePresence } from 'motion/react';
import { SetupWizard } from "./components/SetupWizard.jsx";
import { SecretMenu } from "./components/SecretMenu.jsx";
import { Home } from './components/Home.jsx';
import { Header } from './components/Header.jsx';
import { CategoryFilter } from './components/CategoryFilter';
import { ProductGrid } from './components/ProductGrid';
import { ProductDetailModal } from './components/ProductDetailModal';
import { CrossSell } from './components/CrossSell';
import { InactivityModal } from './components/InactivityModal.jsx';
import { ShoppingCart } from './components/ShoppingCart';
import { FloatingCartNotice } from './components/FloatingCartNotice';
import { CheckoutModal } from './components/CheckoutModal';
import { IdentificationModal } from './components/IdentificationModal';
import { SearchView } from './components/SearchView';
import { CashClosingView } from './components/CashClosingView';
import { VersionTag } from './components/VersionTag';
import { BannerCarousel } from './components/BannerCarousel';
import { Breadcrumbs } from './components/Breadcrumbs.jsx';
import { UnavailableMessage } from './components/UnavailableMessage.jsx';
import { UpdateAlert } from './components/UpdateAlert.jsx';
import { Notification } from './components/Notification.jsx';
import { AccessibilityButton, LowZoneNotice } from './components/AccessibilityMode.jsx';
import { StoreClosedScreen, StoreClosingBar } from './components/StoreClosing.jsx';
import { getProductInfo } from './utils/productHelpers.js';
import { applyPromotions } from './utils/promotions.js';
import { sortModifierGroups, sortModifierOptions, resolveExtraValue } from './utils/modifierHelpers.js';
import { isCategoryOpen } from './utils/schedule.js';
import { formatStoreTime } from './utils/storeHours.js';
import { hasModule, SELF_ORDERING_KIOSK_MODULE } from './utils/modules.js';
import { IDLE_POS_POLL_TICK_MS, POS_CHECKED_BY, POS_POLL_TIMEOUT_ERROR, POS_POLL_TIMEOUT_MS, buildSessionHealth, getPOSProvider, posCheckFromPayment, readNetwork, shouldPollPOSOnIdle, toPaymentTerminal } from './utils/sessionHealth.js';
import { serverNow } from './utils/serverTime.js';
import { Email } from './utils/Email.js';
import { WebServices } from './utils/WebServices.js';
import { updateServiceWorker, unregisterServiceWorker } from "./utils/cacheManager.js";
import { backupConfig, clearConfigBackup, requestPersistentStorage, restoreConfigBackup } from "./utils/configBackup.js";
import { useItems } from './hooks/useItems';
import { useNow } from './hooks/useNow.js';
import { useStoreHours } from './hooks/useStoreHours.js';
import { useTheme } from "./hooks/useTheme.jsx";
import { useModifiers } from "./hooks/useModifiers.js";
import { useZones } from "./hooks/useZones.js";
import { useStores } from "./hooks/useStores.js";
import { useCatalogs } from "./hooks/useCatalogs.js";
import { useCategories } from "./hooks/useCategories.js";
import { useStyles } from "./hooks/useStyles.js";
import { usePlaylists } from './hooks/usePlaylists';
import { useCarousels } from './hooks/useCarousels';
import { useVideos } from './hooks/useVideos';
import { useImages } from './hooks/useImages';
import { useAdmins } from './hooks/useAdmins';
import { useShoppingCart } from './hooks/useShoppingCart';
import { useBanners } from "./hooks/useBanners.js";
import { usePromotions } from "./hooks/usePromotions.js";
import { useLoyaltyPrograms } from "./hooks/useLoyaltyPrograms.js";
import { useLoyalty } from "./hooks/useLoyalty.js";
import { useClient } from "./hooks/useClient.js";
import { useInstance } from "./hooks/useInstance.js";
import { useA11y } from "./hooks/useA11y.js";
import { resolveLowZone } from './utils/a11y.js';
import { SCAN_ACTION, SCAN_ENTITY_TYPE, SCAN_FOUND, SCAN_OPEN_PRODUCT, SCAN_REASON_UNAVAILABLE, SCAN_SOUND_SRC, createBarcodeReader, getScanResult, scanKeyFromEvent } from './utils/scan.js';
import { ScanOverlay } from './components/ScanOverlay.jsx';
import { useConnectivity } from './hooks/useConnectivity.js';
import { usePendingPaymentRecovery } from './hooks/usePendingPaymentRecovery.js';
import { CONNECTIVITY, canStartPayment } from './utils/connectivity.js';
import { checkoutTexts } from './i18n/checkout.es.js';
import { LoaderCircle } from 'lucide-react';
import { preloadItemImages, preloadCategoryImages, preloadBannerImages, preloadPlaylistVideos } from "./utils/preload.js";
import { getCrossSellShownIds } from './utils/suggestions.js';
import appData from '../package.json';
import './App.css'

// View types are: 'home', 'catalog', 'cart', 'search'.

const DEFAULT_INACTIVITY_REDIRECT_TIME = 60 * 1000 * 5; // 5 minutes
const DEFAULT_INACTIVITY_DIALOG_TIME = 60 * 1000 * 4; // 4 minutes
const SHOW_INACTIVITY_LOG = false;
const PING_TIME = 1000 * 60; // 1 minute

function App() {
  const env = import.meta.env.REACT_APP_ENV || "development";
  const scanSoundRef = useRef(null);
  const isScanningRef = useRef(false);
  const scanHandlerRef = useRef(null);
  // Producto cuya vista se abrió desde una lectura para elegir sus modificadores.
  const scannedProductIdRef = useRef(null);
  const rootRef = useRef(null);

  const [currentView, setCurrentView] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allowCrossSellModal, setAllowCrossSellModal] = useState(true);
  const [isCrossSellModalOpen, setIsCrossSellModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isPaymentInFlight, setIsPaymentInFlight] = useState(false);
  const [isIdentifyModalOpen, setIsIdentifyModalOpen] = useState(false);
  const [isSecretMenuModalOpen, setIsSecretMenuModalOpen] = useState(false);
  const [isConnectingPOS, setIsConnectingPOS] = useState(false);
  const [clickSequence, setClickSequence] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [isPollingPOS, setIsPollingPOS] = useState(false);
  // El poll automático (tótem inactivo en inicio) no tapa la pantalla de inicio: solo bloquea si el
  // cliente llega al checkout antes de que termine, porque el POS atiende una tarea a la vez.
  const [isAutomaticPOSPoll, setIsAutomaticPOSPoll] = useState(false);
  const isPOSPollBlocking = isPollingPOS && (!isAutomaticPOSPoll || isCheckoutModalOpen);

  const [latestUpdateSaved, setLatestUpdateSaved] = useState(false);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [paymentErrors, setPaymentErrors] = useState([]);
  const MAX_MIX_QUANTITY = 30;
  const PAYMENT_ERRORS_THRESHOLD = 3;
  const NOTIFICATION_DURATION = 5; // Seconds.

  const saveLatestUpdate = async () => {
    let timestamp;
    try {
      timestamp = await WebServices.getServerTimestamp();
      if (!timestamp) throw new Error('invalid_server_timestamp');
    } catch (error) {
      console.error(`An error occurred while obtaining the latest server timestamp: ${error}`);
      timestamp = new Date().getTime();
    }
    localStorage.setItem('sok_latest_update', timestamp);
    setLatestUpdateSaved(true);
  };

  const checkForUpdate = (instance) => {
    if (!instance || !instance.updateIfBefore || !instance.updateIfBefore.seconds) return;
    const updateIfBefore = instance.updateIfBefore && instance.updateIfBefore.seconds ? (instance.updateIfBefore.seconds * 1000) : null;
    let instanceUpdate = localStorage.getItem('sok_latest_update');
    try {
      instanceUpdate = parseInt(instanceUpdate);
    } catch (error) {
      instanceUpdate = new Date().getTime();
    }
    if (instanceUpdate < updateIfBefore) {
      logActivity('update-alert', 'initialize', 'initialize', false, null);
      setShowUpdateAlert(false);
      setTimeout(() => {
        setShowUpdateAlert(true);
      }, 1000);
    }
  };

  const closeUpdateAlert = () => {
    logActivity('update-alert', 'close-button', 'close', true, null);
    setShowUpdateAlert(false);
  };

  const reloadApp = async () => {
    try {
      try {
        await unregisterServiceWorker();
        await updateServiceWorker();
      } catch (error) {
        console.error('[SecretMenu] Error updating service worker (ignored):', error);
      }
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  useEffect(() => {
    saveLatestUpdate();
    document.addEventListener('contextmenu', function (event) {
      event.preventDefault();
    });
    setInteractionListeners();
    return () => {
      removeInteractionListeners();
    }
  }, []);

  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem("kiosk_config");
    return saved ? JSON.parse(saved) : null;
  });
  // Sin configuración en localStorage se busca primero el respaldo de IndexedDB (ver
  // utils/configBackup.js); mientras tanto se muestra la carga y no el asistente.
  const [isRestoringConfig, setIsRestoringConfig] = useState(() => !localStorage.getItem("kiosk_config"));

  useEffect(() => {
    requestPersistentStorage();
    if (config) {
      backupConfig(config);
      return;
    }
    restoreConfigBackup().then((restored) => {
      if (!restored) {
        setIsRestoringConfig(false);
        return;
      }
      // Se recarga para arrancar limpio con la configuración recuperada, igual que tras el asistente.
      localStorage.setItem("kiosk_config", JSON.stringify(restored));
      window.location.reload();
    });
  // Solo al arrancar: después, saveConfig y handleResetConfig mantienen el respaldo al día.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = config?.client || null;
  const [isLoadingData, setIsloadingData] = useState(true);
  const { theme, setTheme } = useTheme();
  const now = useNow();
  const { loadedItems, items } = useItems(env, client);
  const { loadedModifiers, modifiers } = useModifiers(env, client);
  const { loadedStyles, styles } = useStyles(env, client);
  const { loadedZones, zones } = useZones(env, client);
  const { loadedStores, stores } = useStores(env, client);
  const { loadedCatalogs, catalogs } = useCatalogs(env, client);
  const { loadedCategories, categories } = useCategories(env, client);
  const { loadedPlaylists, playlists } = usePlaylists(env, client);
  const { loadedCarousels, carousels } = useCarousels(env, client);
  const { loadedVideos, videos } = useVideos(env, client);
  const { loadedImages, images } = useImages(env, client);
  const { loadedBanners, banners } = useBanners(env, client);
  const { loadedClient, clientData } = useClient(env, client);
  const { loadedInstance, instance } = useInstance(env, client, config?.instance);
  const { loadedAdmins, admins } = useAdmins(env, client);
  const { loadedPromotions, promotions } = usePromotions(env, client);
  const { loyaltyPrograms } = useLoyaltyPrograms(env, client);
  const { identifiedPrograms, identifiedMember, identify, resetLoyalty } = useLoyalty(env, client);
  const { active: a11yActive, settings: a11ySettings, reset: resetA11y } = useA11y();

  const [sessionId, setSessionId] = useState(null);
  const instanceId = instance?.id || null;
  const catalogId = instance?.catalog || null;
  const storeId = instance?.store || null;
  const playlistId = instance?.playlist || null;

  const pingTimer = useRef(null);
  const sessionQueue = useRef(Promise.resolve());
  const inactivityTimer = useRef(null);
  // SOK-33: estado que viaja en el ping de `sessions`. Va en una ref porque el intervalo del ping
  // conserva la versión de sendPing con la que se creó.
  const sessionHealthRef = useRef({ screen: 'home', lastError: null, paymentTerminal: null });
  // Hay un poll del POS en curso; evita lanzar dos a la vez.
  const posPollInFlightRef = useRef(false);
  // Último toque o lectura, para saber si la pantalla inicial está inactiva.
  const lastInteractionRef = useRef(Date.now());
  // Versión al día de pollPOSOnIdle para el intervalo, que no ve los estados nuevos.
  const idlePOSPollRef = useRef(null);
  const [inactivityCounter, setInactivityCounter] = useState(0);
  const [showInactivityDialog, setShowInactivityDialog] = useState(false);
  const [checkoutRemoteClear, setCheckoutRemoteClear] = useState(false);
  const [inactivityRedirectTimeout, setInactivityRedirectTimeout] = useState(null);
  const [inactivityDialogTimeout, setInactivityDialogTimeout] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  useEffect(() => {
    setInactivityRedirectTimeout(styles?.inactivityRedirectTime || DEFAULT_INACTIVITY_REDIRECT_TIME);
    setInactivityDialogTimeout(styles?.inactivityDialogTime || DEFAULT_INACTIVITY_DIALOG_TIME);
  }, [styles]);

  // SOK-09: alto de la zona baja de este tótem (`instances/{id}.a11y.lowZonePx`).
  useEffect(() => {
    document.documentElement.style.setProperty('--a11y-low-zone', resolveLowZone(instance));
  }, [instance]);

  useEffect(() => {
    if (loadedItems && loadedModifiers && loadedStores && loadedCatalogs && loadedCategories && loadedStyles && loadedPlaylists && loadedCarousels && loadedVideos && loadedImages && loadedBanners && loadedPromotions && loadedInstance && loadedZones && loadedAdmins && loadedClient) {
      // Sin configuración no hay nada que cargar: el asistente se muestra de inmediato.
      setTimeout(() => {
        setIsloadingData(false);
      }, config ? 1000 : 0);
    };
  }, [loadedItems, loadedModifiers, loadedStores, loadedCatalogs, loadedCategories, loadedStyles, loadedPlaylists, loadedCarousels, loadedVideos, loadedImages, loadedBanners, loadedPromotions, loadedInstance, loadedZones, loadedAdmins, loadedClient]);

  useEffect(() => {
    if (items && items.length > 0) preloadItemImages(items);
  }, [items]);

  const {
    cart,
    cartId,
    addOrUpdateItem,
    updateQuantity,
    updateItem,
    removeItem,
    newCart,
    setOrderStatus,
    setLoading,
    pollingViaAPI,
    checkOrderStatus,
    checkoutAndWaitStatusViaAPI,
    resumePendingPayment,
    checkPendingPaymentInBackground,
    getTotals,
    loading: cartLoading,
  } = useShoppingCart(env, client, storeId, instanceId, identifiedMember);

  // SOK-17: el escáner escribe como un teclado. Las teclas se juntan en un buffer fuera de React
  // (antes cada dígito pasaba por el estado y renderizaba toda la app, lo que en el tótem sumaba
  // segundos por lectura). El listener se registra una vez y lee el estado vigente desde
  // `scanHandlerRef`, que se actualiza en cada render.
  useEffect(() => {
    const reader = createBarcodeReader({
      onScan: (code) => scanHandlerRef.current?.onScan(code),
      isBusy: () => isScanningRef.current,
    });
    const onKeyDown = (event) => {
      const handler = scanHandlerRef.current;
      if (!handler) return;
      if (handler.blockKeys) {
        event.preventDefault();
        reader.reset();
        return;
      }
      if (!handler.canScan) {
        reader.reset();
        return;
      }
      reader.handleKey(scanKeyFromEvent(event));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      reader.reset();
    };
  }, []);

  // KIO-12: el bip se precarga una vez para que suene sin demora en la primera lectura.
  useEffect(() => {
    try {
      const audio = new Audio(SCAN_SOUND_SRC);
      audio.preload = 'auto';
      audio.load();
      scanSoundRef.current = audio;
    } catch {
      scanSoundRef.current = null;
    }
  }, []);

  // El navegador puede bloquear el audio sin interacción previa (autoplay); en ese caso se sigue
  // sin sonido y sin ensuciar la consola.
  const playScanSound = () => {
    const audio = scanSoundRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      const playing = audio.play();
      if (playing && typeof playing.catch === 'function') playing.catch(() => {});
    } catch {
      // Sin sonido.
    }
  };

  // Mientras dura la lectura la pantalla se oscurece y no acepta toques ni otra lectura
  // (ScanOverlay). Se espera un cuadro para que el overlay se pinte antes de buscar y se mantiene
  // un mínimo para que se note.
  const SCAN_OVERLAY_MIN_MS = 400;

  const handleScan = async (barcode) => {
    if (isScanningRef.current) return;
    isScanningRef.current = true;
    setIsScanning(true);
    const startedAt = performance.now();
    try {
      playScanSound();
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
      const { product, entityId, metadata } = getScanResult(validProducts, barcode, {
        quantity: 1,
        allProducts: items,
        hasModifiers: (item) => getSelectedProductModifiers(item.id).length > 0,
      });
      logActivity(SCAN_ENTITY_TYPE, entityId, SCAN_ACTION, true, metadata);
      if (entityId === SCAN_FOUND) {
        handleAddToCart(product, 1, {});
        handleCartClick();
      } else if (entityId === SCAN_OPEN_PRODUCT) {
        handleProductClick(product);
        scannedProductIdRef.current = product.id;
      } else {
        const id = new Date().getTime();
        const message = metadata.reason === SCAN_REASON_UNAVAILABLE
          ? `<b>${metadata.product_name || 'El producto'}</b> no está disponible en este momento.`
          : `No encontramos ningún producto con el código <b>${barcode}</b>.`;
        addNotification(id, 'neutral', message, null, null);
        setTimeout(() => { removeNotification(id) }, NOTIFICATION_DURATION * 1000);
      }
    } catch (error) {
      console.error("Scan Error:", error);
      const id = new Date().getTime();
      addNotification(id, 'neutral', 'No pudimos leer el código. Inténtalo de nuevo.', null, null);
      setTimeout(() => { removeNotification(id) }, NOTIFICATION_DURATION * 1000);
    } finally {
      const remaining = SCAN_OVERLAY_MIN_MS - (performance.now() - startedAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      isScanningRef.current = false;
      setIsScanning(false);
    }
  };

  // En el menú secreto el teclado queda bloqueado, salvo al vincular el POS (el escáner escribe el
  // número de serie). Se lee en cada tecla desde el listener del escáner.
  useEffect(() => {
    scanHandlerRef.current = {
      blockKeys: isPOSPollBlocking || (isSecretMenuModalOpen && !isConnectingPOS),
      // SOK-37: con el local cerrado no se escanea (no se puede agregar nada al carro).
      canScan: Boolean(config) && storeHours.open && !isPOSPollBlocking && !isSecretMenuModalOpen && ['home', 'catalog', 'cart'].includes(currentView) && !isCheckoutModalOpen && !isIdentifyModalOpen,
      onScan: handleScan,
    };
  });

  // Depende del id y no del objeto `instance`: Firestore lo entrega dos veces al iniciar (caché y
  // servidor) y cada entrega creaba una sesión, dejando una activa sin ping que bloqueaba la
  // instancia en el asistente.
  useEffect(() => {
    if (client && instanceId && latestUpdateSaved) createSession();
  }, [client, instanceId, latestUpdateSaved]);

  useEffect(() => {
    if (instance && latestUpdateSaved) checkForUpdate(instance);
  }, [instance, latestUpdateSaved]);

  useEffect(() => {
    shouldStartInactivityTimer(instance, currentView, inactivityRedirectTimeout, inactivityDialogTimeout);
  }, [instance, currentView, inactivityRedirectTimeout, inactivityDialogTimeout]);

  useEffect(() => {
    sessionHealthRef.current.screen = currentView;
  }, [currentView]);

  useEffect(() => {
    const options = { capture: true, passive: true };
    const touch = () => { lastInteractionRef.current = Date.now(); };
    const events = ['pointerdown', 'touchstart', 'keydown'];
    events.forEach((type) => window.addEventListener(type, touch, options));
    return () => events.forEach((type) => window.removeEventListener(type, touch, options));
  }, []);

  useEffect(() => {
    idlePOSPollRef.current = pollPOSOnIdle;
  });

  useEffect(() => {
    if (!sessionId) return;
    const timer = setInterval(() => idlePOSPollRef.current?.(), IDLE_POS_POLL_TICK_MS);
    return () => clearInterval(timer);
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) startPingTimer();
    else stopPingTimer();
  }, [sessionId]);

  // Las creaciones de sesión se encadenan: dos en paralelo cerrarían las activas antes de que la
  // otra exista y ambas quedarían abiertas.
  const createSession = () => {
    sessionQueue.current = sessionQueue.current.then(createSessionNow, createSessionNow);
    return sessionQueue.current;
  };

  const createSessionNow = async () => {
    await closeSession();
    const data = {
      client: client,
      instance: instanceId,
      catalog: catalogId,
      store: storeId,
      active: true,
      started_at: serverTimestamp(),
      finished_at: null,
      last_ping: serverTimestamp(),
      version: appData.version
    };
    const _collection = collection(db, `kiosk/${env}/client/${client}/sessions`);
    try {
      const reference = await addDoc(_collection, data);
      setSessionId(reference.id);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const closeSession = async () => {
    if (!instance) return false;
    const now = serverTimestamp();
    const data = { active: false, finished_at: now, last_ping: now };
    try {
      const sessionsRef = collection(db, `kiosk/${env}/client/${client}/sessions`);
      const activeSessionsQuery = query(sessionsRef, where('instance', '==', instanceId), where('active', '==', true));
      const querySnapshot = await getDocs(activeSessionsQuery);
      if (querySnapshot.empty) {
        setSessionId(null);
        return true;
      }
      const batch = writeBatch(db);
      querySnapshot.forEach((document) => { batch.update(document.ref, data) });
      await batch.commit();
      setSessionId(null);
      return true;
    } catch (error) {
      console.error('Error closing user sessions:', error);
      return false;
    }
  };

  const sendPing = async () => {
    if (!sessionId) return false;
    const now = serverTimestamp();
    const health = sessionHealthRef.current;
    const data = {
      active: true,
      last_ping: now,
      version: appData.version,
      ...buildSessionHealth({ network: readNetwork(navigator), screen: health.screen, lastError: health.lastError, paymentTerminal: health.paymentTerminal }),
    };
    const _document = doc(db, `kiosk/${env}/client/${client}/sessions/${sessionId}`);
    try {
      await updateDoc(_document, data);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const startPingTimer = () => {
    stopPingTimer();
    sendPing();
    updateHistoryIfNecessary();
    pingTimer.current = setInterval(() => {
      sendPing();
      updateHistoryIfNecessary();
    }, PING_TIME);
  };

  const stopPingTimer = () => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
  };

  const updateHistoryIfNecessary = async () => {
    if (!instance) return;
    const _path = `kiosk/${env}/client/${client}/history`;
    const _query = query(collection(db, _path), where('instance', '==', instanceId), orderBy("timestamp", "desc"), limit(1));
    try {
      const querySnapshot = await getDocs(_query);
      let lastEvent;
      if (!querySnapshot.empty) {
        querySnapshot.forEach((document) => {
          lastEvent = { id: document.id, data: document.data() };
        });
      }
      if (!lastEvent || (lastEvent && lastEvent.data && lastEvent.data.status === 'offline')) {
        const newEvent = { instance: instanceId, status: 'online', timestamp: serverTimestamp() };
        const _collection = collection(db, _path);
        return addDoc(_collection, newEvent);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const shouldStartInactivityTimer = (instance, currentView, inactivityRedirectTimeout, inactivityDialogTimeout) => {
    if (
      instance && currentView !== 'home' &&
      inactivityRedirectTimeout != null && inactivityRedirectTimeout > 0 &&
      inactivityDialogTimeout != null && inactivityDialogTimeout > 0
    ) {
      startInactivityTimer();
    } else {
      stopInactivityTimer();
    }
  };

  const startInactivityTimer = () => {
    stopInactivityTimer();
    inactivityTimer.current = setInterval(() => {
      setInactivityCounter(c => c + 1000);
    }, 1000);
  };

  const stopInactivityTimer = () => {
    if (inactivityTimer.current) {
      clearInterval(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  };

  const resetIdleModeManagerTimer = () => {
    setInactivityCounter(0);
  };

  useEffect(() => {
    if (inactivityTimer && inactivityTimer.current) {
      if (SHOW_INACTIVITY_LOG) console.log(`Inactivity Tick: ${inactivityCounter}`);
      if (inactivityCounter >= inactivityDialogTimeout && !showInactivityDialog) {
        if (SHOW_INACTIVITY_LOG) console.log('Open inactivity dialog');
        logActivity('inactivity-dialog', 'trigger', 'open', false, null);
        setShowInactivityDialog(true);
      }
      if (inactivityCounter >= inactivityRedirectTimeout) {
        if (SHOW_INACTIVITY_LOG) console.log('Redirect to home');
        logActivity('inactivity-timer', 'trigger', 'reset', false, null);
        setShowInactivityDialog(false);
        setInactivityCounter(0);
        setIsModalOpen(false);
        setCurrentView('home');
        handleClearCart();
        setCheckoutRemoteClear(true);
        setIsSecretMenuModalOpen(false);
        setIsCrossSellModalOpen(false);
        setAllowCrossSellModal(true);
        setTimeout(() => { setCheckoutRemoteClear(false), 10 });
      }
    }
  }, [inactivityCounter]);

  const setInteractionListeners = () => {
    const options = { capture: true, passive: false };
    window.addEventListener('mousemove', resetIdleModeManagerTimer, options);
    window.addEventListener('mousedown', resetIdleModeManagerTimer, options);
    window.addEventListener('touchstart', resetIdleModeManagerTimer, options);
    window.addEventListener('click', resetIdleModeManagerTimer, options);
    window.addEventListener('scroll', resetIdleModeManagerTimer, options);
    window.addEventListener('keypress', resetIdleModeManagerTimer, options);
  };

  const removeInteractionListeners = () => {
    const options = { capture: true, passive: false };
    window.removeEventListener('mousemove', resetIdleModeManagerTimer, options);
    window.removeEventListener('mousedown', resetIdleModeManagerTimer, options);
    window.removeEventListener('touchstart', resetIdleModeManagerTimer, options);
    window.removeEventListener('click', resetIdleModeManagerTimer, options);
    window.removeEventListener('scroll', resetIdleModeManagerTimer, options);
    window.removeEventListener('keypress', resetIdleModeManagerTimer, options);
  };

  const logActivity = async (entityType, entityId, action, fromUser, metadata) => {
    try {
      if (!client || !instanceId || !catalogId || !storeId || !sessionId || !entityType || !entityId || !action) {
        console.error('error', 'Activity.log() is missing required data.');
        console.error('error', { client: client, instance: instanceId, catalog: catalogId, store: storeId, session: sessionId, entityType, entityId, action, fromUser, metadata });
        return false;
      };
      const data = {
        client: client,
        instance: instanceId,
        catalog: catalogId,
        store: storeId,
        session: sessionId,
        source: 'kiosk',
        entity_type: entityType,
        entity_id: entityId || null,
        action: action,
        from_user: fromUser,
        metadata: metadata ? sanitizeData(metadata) : null,
        timestamp: serverTimestamp()
      };
      const _collection = collection(db, 'kiosk', env, 'client', client, 'activity');
      await addDoc(_collection, data);
      return true;
    } catch (error) {
      console.error('error', error);
      return false;
    }
  };

  const sanitizeData = (data) => {
    if (typeof data === 'object' && data !== null) {
      Object.keys(data).forEach((key) => {
        data[key] = sanitizeData(data[key]);
      });
    } else if (Array.isArray(data)) data = data.map((item) => sanitizeData(item));
    else if (data === undefined) data = null;
    return data;
  };

  const cartItems = useMemo(() => {
    if (!cart || !cart.details) return [];
    return cart.details;
  }, [cart]);

  const playlist = useMemo(() => {
    if (playlists && playlistId) {
      const currentPlaylist = playlists[playlistId];
      return currentPlaylist && currentPlaylist.version && currentPlaylist.version === '2.0' ? currentPlaylist : null;
    } else {
      return null;
    }
  }, [playlists, playlistId]);

  useEffect(() => {
    if (playlist) preloadPlaylistVideos(playlist, videos);
  }, [playlist]);

  const currentStore = useMemo(() => {
    if (!stores || !storeId) return null;
    const store = stores.find((s) => s.id === storeId) || null;
    return store;
  }, [storeId, stores]);

  const filteredStoreIds = useMemo(() => {
    if (!stores || !storeId) return [];
    return stores.filter((s) => {
      return s.id === (storeId || null);
    }).map(s => {
      const backup_stores = s.backup_stores || [];
      if (!backup_stores.includes(s.id)) {
        backup_stores.unshift(s.id);
      }
      return backup_stores
    }).flat();
  }, [storeId, stores]);

  const filteredZones = useMemo(() => {
    if (!config || !zones) return [];
    return zones.filter((z) => {
      const store_ids = z.store_ids || [];
      if (!store_ids.includes(config?.store || null)) return false;
      return true;
    });
  }, [config, zones]);

  const filteredCatalog = useMemo(() => {
    if (!catalogs || !catalogId) return [];
    return catalogs.find((c) => c.id === catalogId);
  }, [catalogId, catalogs]);

  const sellers = useMemo(() => {
    if (!items) return [];
    const sellerSet = new Set();
    items.forEach(item => {
      const sellerName = item.seller_name || null;
      if (sellerName) sellerSet.add(sellerName);
    });
    return Array.from(sellerSet);
  }, [items]);

  const sellerColors = [
    "linear-gradient(to top, #E6194B, #E6194B33)", "linear-gradient(to top, #3CB44B, #3CB44B33)", "linear-gradient(to top, #4363D8, #4363D833)", "linear-gradient(to top, #F58231, #F5823133)", "linear-gradient(to top, #911EB4, #911EB433)",
    "linear-gradient(to top, #42D4F4, #42D4F433)", "linear-gradient(to top, #F032E6, #F032E633)", "linear-gradient(to top, #BFEF45, #BFEF4533)", "linear-gradient(to top, #FABED4, #FABED433)", "linear-gradient(to top, #469990, #46999033)",
    "linear-gradient(to top, #DCBEFF, #DCBEFF33)", "linear-gradient(to top, #9A6324, #9A632433)", "linear-gradient(to top, #FFFAC8, #FFFAC833)", "linear-gradient(to top, #800000, #80000033)", "linear-gradient(to top, #AAFFC3, #AAFFC333)",
    "linear-gradient(to top, #808000, #80800033)", "linear-gradient(to top, #FFD8B1, #FFD8B133)", "linear-gradient(to top, #000075, #00007533)", "linear-gradient(to top, #A9A9A9, #A9A9A933)", "linear-gradient(to top, #FFFFFF, #FFFFFF33)",
    "linear-gradient(to top, #FF0000, #FF000033)", "linear-gradient(to top, #00FF00, #00FF0033)", "linear-gradient(to top, #0000FF, #0000FF33)", "linear-gradient(to top, #FFFF00, #FFFF0033)", "linear-gradient(to top, #FF00FF, #FF00FF33)",
    "linear-gradient(to top, #00FFFF, #00FFFF33)", "linear-gradient(to top, #FF8000, #FF800033)", "linear-gradient(to top, #8000FF, #8000FF33)", "linear-gradient(to top, #0080FF, #0080FF33)", "linear-gradient(to top, #FF0080, #FF008033)",
    "linear-gradient(to top, #00FF80, #00FF8033)", "linear-gradient(to top, #80FF00, #80FF0033)", "linear-gradient(to top, #FF4040, #FF404033)", "linear-gradient(to top, #40FF40, #40FF4033)", "linear-gradient(to top, #4040FF, #4040FF33)",
    "linear-gradient(to top, #FF8040, #FF804033)", "linear-gradient(to top, #40FF80, #40FF8033)", "linear-gradient(to top, #8040FF, #8040FF33)", "linear-gradient(to top, #FF4080, #FF408033)", "linear-gradient(to top, #80FF40, #80FF4033)",
    "linear-gradient(to top, #4080FF, #4080FF33)", "linear-gradient(to top, #FF6600, #FF660033)", "linear-gradient(to top, #00FF66, #00FF6633)", "linear-gradient(to top, #6600FF, #6600FF33)", "linear-gradient(to top, #FF0066, #FF006633)",
    "linear-gradient(to top, #66FF00, #66FF0033)", "linear-gradient(to top, #0066FF, #0066FF33)", "linear-gradient(to top, #CC0000, #CC000033)", "linear-gradient(to top, #00CC00, #00CC0033)", "linear-gradient(to top, #0000CC, #0000CC33)",
    "linear-gradient(to top, #CC6600, #CC660033)", "linear-gradient(to top, #00CC66, #00CC6633)", "linear-gradient(to top, #6600CC, #6600CC33)", "linear-gradient(to top, #CC0066, #CC006633)", "linear-gradient(to top, #66CC00, #66CC0033)",
    "linear-gradient(to top, #0066CC, #0066CC33)", "linear-gradient(to top, #990000, #99000033)", "linear-gradient(to top, #009900, #00990033)", "linear-gradient(to top, #000099, #00009933)", "linear-gradient(to top, #996600, #99660033)",
    "linear-gradient(to top, #009966, #00996633)", "linear-gradient(to top, #660099, #66009933)", "linear-gradient(to top, #990066, #99006633)", "linear-gradient(to top, #669900, #66990033)", "linear-gradient(to top, #006699, #00669933)",
    "linear-gradient(to top, #FF9999, #FF999933)", "linear-gradient(to top, #99FF99, #99FF9933)", "linear-gradient(to top, #9999FF, #9999FF33)", "linear-gradient(to top, #FFCC99, #FFCC9933)", "linear-gradient(to top, #99FFCC, #99FFCC33)",
    "linear-gradient(to top, #CC99FF, #CC99FF33)", "linear-gradient(to top, #FF99CC, #FF99CC33)", "linear-gradient(to top, #CCFF99, #CCFF9933)", "linear-gradient(to top, #99CCFF, #99CCFF33)", "linear-gradient(to top, #FFFF99, #FFFF9933)",
    "linear-gradient(to top, #99FFFF, #99FFFF33)", "linear-gradient(to top, #FF99FF, #FF99FF33)", "linear-gradient(to top, #CC3300, #CC330033)", "linear-gradient(to top, #00CC33, #00CC3333)", "linear-gradient(to top, #3300CC, #3300CC33)",
    "linear-gradient(to top, #CC0033, #CC003333)", "linear-gradient(to top, #33CC00, #33CC0033)", "linear-gradient(to top, #0033CC, #0033CC33)", "linear-gradient(to top, #FF3300, #FF330033)", "linear-gradient(to top, #00FF33, #00FF3333)",
    "linear-gradient(to top, #3300FF, #3300FF33)", "linear-gradient(to top, #FF0033, #FF003333)", "linear-gradient(to top, #33FF00, #33FF0033)", "linear-gradient(to top, #0033FF, #0033FF33)", "linear-gradient(to top, #884400, #88440033)",
    "linear-gradient(to top, #008844, #00884433)", "linear-gradient(to top, #440088, #44008833)", "linear-gradient(to top, #880044, #88004433)", "linear-gradient(to top, #448800, #44880033)", "linear-gradient(to top, #004488, #00448833)",
    "linear-gradient(to top, #663300, #66330033)", "linear-gradient(to top, #006633, #00663333)", "linear-gradient(to top, #330066, #33006633)", "linear-gradient(to top, #660033, #66003333)", "linear-gradient(to top, #336600, #33660033)",
    "linear-gradient(to top, #003366, #00336633)", "linear-gradient(to top, #222222, #22222233)", "linear-gradient(to top, #555555, #55555533)", "linear-gradient(to top, #888888, #88888833)", "linear-gradient(to top, #BBBBBB, #BBBBBB33)",
  ];

  const isItemValid = (item, checkMaxPerItem) => {
    return filteredStoreIds.some((storeId) => 
      item &&
      item.info_stores &&
      item.info_stores[storeId] && 
      item.info_stores[storeId].is_available && 
      (!checkMaxPerItem || item.info_stores[storeId].max_per_item > 0) &&
      item.info_stores[storeId].prices && 
      item.info_stores[storeId].prices.length > 0
    );
  };

  const itemRequiresModifiers = (item) => {
    if (!item.modifiers || item.modifiers.length === 0) return false;
    if (!modifiers) return false;
    const requiredModifiers = [];
    (item.modifiers || []).forEach((itemModifier) => {
      const match = modifiers.find((modifier) => modifier.id === itemModifier);
      if (match && (match.min || 0) >= 1) requiredModifiers.push(match);
    });
    return requiredModifiers.length > 0;
  };

  const itemHasValidModifiers = (item) => {
    const requiredModifiers = [];
    (item.modifiers || []).forEach((itemModifier) => {
      if (!modifiers) return null;
      const match = modifiers.find((modifier) => modifier.id === itemModifier);
      if (match && (match.min || 0) >= 1) requiredModifiers.push({modifier: match, valid: false});
    });
    if (!requiredModifiers || requiredModifiers.length === 0) return true;
    if (requiredModifiers.length > 0) {
      for (let i = 0; i < requiredModifiers.length; i++) {
        const {modifier} = requiredModifiers[i];
        let hasAnyOption = false;
        (modifier.options || []).forEach((option) => {
          switch (option.type) {
            case "item": {
              const itemId = option.id || null;
              const item = itemId ? items.find((p) => p.id === itemId) : null;
              if (item && isItemValid(item, true)) hasAnyOption = true;
              break;
            }
            case "modifier":
              hasAnyOption = true;
              break;
            default:
              hasAnyOption = true;
              break;
          }
        });
        if (hasAnyOption) requiredModifiers[i].valid = true;
      }
    }
    return requiredModifiers.every((rm) => rm.valid);
  };

  // El cliente puede dar de baja el módulo mientras el tótem está en uso. useClient escucha el
  // documento en vivo, así que el mensaje de no disponible aparece solo con que cambie `modules`,
  // sin necesidad de reiniciar el equipo.
  const hasSelfOrderingModule = useMemo(() => {
    return hasModule(clientData, SELF_ORDERING_KIOSK_MODULE);
  }, [clientData]);

  const catalogCategoryIds = useMemo(() => {
    return new Set(filteredCatalog?.categories || []);
  }, [filteredCatalog]);

  const closedCategoryIds = useMemo(() => {
    if (!categories) return new Set();
    return new Set(categories.filter((category) => !isCategoryOpen(category, now)).map((category) => category.id));
  }, [categories, now]);

  // Un producto sigue a la venta mientras le quede alguna categoría abierta dentro del catálogo que
  // este kiosko está leyendo: la botella cuya categoría BOTELLA cerró se sigue mostrando bajo
  // WHISKY. Solo cuando todas las categorías del catálogo por las que se llega a él están fuera de
  // horario el producto deja de existir para toda la app, y entonces tampoco sale en "Todos", ni en
  // la búsqueda, ni como opción de un modificador, ni al escanear su código.
  const isItemInSchedule = (item) => {
    if (closedCategoryIds.size === 0) return true;
    const reachableCategories = (item?.categories || []).filter((categoryId) => catalogCategoryIds.has(categoryId));
    if (reachableCategories.length === 0) return true;
    return reachableCategories.some((categoryId) => !closedCategoryIds.has(categoryId));
  };

  const validProducts = useMemo(() => {
    if (!items) return [];
    return items.filter(item => {
      if (!isItemInSchedule(item)) return false;
      return itemRequiresModifiers(item) ? itemHasValidModifiers(item) && isItemValid(item, false) : isItemValid(item, true);
    });
  }, [items, filteredStoreIds, closedCategoryIds, catalogCategoryIds]);

  const validModifiers = useMemo(() => {
    if (!modifiers) return [];
    let response = [];
    modifiers.forEach((modifier) => {
      let modifierCopy = {...modifier};
      modifierCopy.options = [];
      (modifier.options || []).forEach((option) => {
        let optionCopy = {...option};
        let isValidOption = false;
        switch (option.type) {
          case "item": {
            const productId = option.id || null;
            isValidOption = productId ? validProducts.some((product) => product.id === productId) : false;
            break;
          }
          case "modifier":
            isValidOption = true;
            break;
          default:
            isValidOption = true;
            break;
        }
        if (isValidOption) modifierCopy.options.push(optionCopy);
      });
      if (modifierCopy.options.length > 0) response.push(modifierCopy);
    });
    return response;
  }, [modifiers, validProducts]);

  const filteredCategoriesRef = useRef([]);

  const shouldShowCategories = useMemo(() => {
    return filteredCatalog && filteredCatalog.categories && filteredCatalog.categories.length > 1 ? true : false;
  }, [filteredCatalog]);

  const validCategories = useMemo(() => {
    if (!categories || !validProducts) return [];
    return categories.filter((category) => {
      if (closedCategoryIds.has(category.id)) return false;
      if (category.id === 'todos' && category.is_visible === true) return true;
      return category.is_visible && !category.is_empty && validProducts.some((product) => product.categories.includes(category.id));
    });
  }, [categories, validProducts, closedCategoryIds]);

  const selectedCategoryData = useMemo(() => {
    if (!validCategories) return null;
    return validCategories.find(c => c.id === selectedCategory) || null;
  }, [selectedCategory, validCategories]);

  let style = theme === "dark" ? styles?.styles?.dark : styles?.styles;
  if (!style) style = styles?.styles;

  // SOK-04: sin red no se inicia un cobro. El catálogo y el carro siguen con la caché de Firestore.
  // Cada cambio queda en activity (addDoc lo deja en cola si no hay red) con cuánto duró el anterior.
  // Al cliente se le avisa con las notificaciones de siempre (al caerse y al volver la red); el aviso
  // que se queda mientras dure la caída es el del botón de pago. No se avisa en la pantalla inicial ni
  // con el modal de pago abierto: ahí el botón ya lo dice, la notificación taparía el modal (con
  // "Pantalla baja" quedan en la misma zona) y durante un cobro "el pago se habilitará" confundiría.
  const offlineNotificationRef = useRef(null);
  const showConnectivityNotification = (type, message) => {
    const id = Date.now();
    addNotification(id, type, message, null, null);
    setTimeout(() => { removeNotification(id) }, NOTIFICATION_DURATION * 1000);
    return id;
  };
  const connectivity = useConnectivity(style, ({ status, previous, durationMs, latencyMs }) => {
    logActivity('connectivity', status, 'change', false, {
      previous,
      previous_duration_ms: durationMs,
      ...(previous === CONNECTIVITY.OFFLINE ? { offline_ms: durationMs } : {}),
      latency_ms: latencyMs,
      current_view: currentView,
      payment_in_flight: isPaymentInFlight,
    });
    const canNotify = currentView !== 'home' && !isCheckoutModalOpen && !isPaymentInFlight;
    if (status === CONNECTIVITY.OFFLINE) {
      if (canNotify) offlineNotificationRef.current = showConnectivityNotification('error', checkoutTexts.connectivity.offlineNotification);
    } else if (previous === CONNECTIVITY.OFFLINE && offlineNotificationRef.current) {
      // Solo se avisa la vuelta a quien vio el aviso de la caída.
      removeNotification(offlineNotificationRef.current);
      offlineNotificationRef.current = null;
      if (canNotify) showConnectivityNotification('success', checkoutTexts.connectivity.restoredNotification);
    }
  });

  // SOK-37: cierre del local por horario (`store.schedule`) o por fin de evento (`store.closing_at`).
  // Antes del cierre, una barra con la cuenta regresiva empuja la app hacia abajo. Al cerrar, una
  // compra en curso (carro con productos fuera del inicio, modal de pago abierto o cobro en vuelo,
  // reintentos incluidos) se deja terminar sin poder agregar más; al terminar queda la pantalla de
  // tienda cerrada. El cierre de caja sigue disponible desde el menú secreto.
  const isPurchaseInProgress = isPaymentInFlight || isCheckoutModalOpen || (cartItems.length > 0 && currentView !== 'home');
  const storeHours = useStoreHours(currentStore, clientData, (next, previous) => {
    logActivity('store-hours', next.open ? 'open' : next.reason, 'change', false, {
      previous: previous.open ? 'open' : previous.reason,
      closes_at: next.closesAt ? next.closesAt.toISOString() : null,
      opens_at: next.opensAt ? next.opensAt.toISOString() : null,
      purchase_in_progress: isPurchaseInProgress,
    });
  });
  const showStoreClosed = !storeHours.open && !isPurchaseInProgress && currentView !== 'cash_closing';
  const showStoreClosingBar = !showStoreClosed && currentView !== 'cash_closing' && (storeHours.closingSoon || !storeHours.open);
  const closingNoticeRef = useRef(null);

  useEffect(() => {
    if (showStoreClosingBar) document.documentElement.setAttribute('data-store-closing', 'on');
    else document.documentElement.removeAttribute('data-store-closing');
  }, [showStoreClosingBar]);

  // El aviso con la hora de cierre sale una vez por cliente: al salir del inicio dentro del plazo de
  // aviso, o al empezar el plazo con alguien comprando. Volver al inicio lo deja listo para el próximo.
  useEffect(() => {
    if (currentView === 'home') {
      closingNoticeRef.current = null;
      return;
    }
    if (!storeHours.closingSoon || currentView === 'cash_closing') return;
    const key = storeHours.closesAt.getTime();
    if (closingNoticeRef.current === key) return;
    closingNoticeRef.current = key;
    const id = Date.now();
    addNotification(id, 'info', `La tienda cierra a las <b>${formatStoreTime(storeHours.closesAt, storeHours.timeZone)}</b>. Termina tu compra antes del cierre.`, null, null);
    setTimeout(() => { removeNotification(id) }, NOTIFICATION_DURATION * 2 * 1000);
  }, [storeHours.closingSoon, storeHours.closesAt, storeHours.timeZone, currentView]);

  // Al mostrar la tienda cerrada no queda nada abierto encima ni el modo accesible del cliente que se fue.
  useEffect(() => {
    if (!showStoreClosed) return;
    setIsModalOpen(false);
    setIsCrossSellModalOpen(false);
    setIsIdentifyModalOpen(false);
    setShowInactivityDialog(false);
    setNotifications([]);
    setCurrentView('home');
    resetA11y();
  }, [showStoreClosed, resetA11y]);

  const notifyStoreClosed = () => {
    const id = Date.now();
    addNotification(id, 'neutral', 'La tienda ya cerró: solo puedes pagar lo que tienes en el carro.', null, null);
    setTimeout(() => { removeNotification(id) }, NOTIFICATION_DURATION * 1000);
  };

  // SOK-04 (punto 5): cobros que quedaron sin confirmar (red caída, espera agotada, app recargada) se
  // vuelven a consultar con red y sin cobro en pantalla. Nunca se cobra de nuevo.
  usePendingPaymentRecovery({
    enabled: connectivity === CONNECTIVITY.ONLINE && !isCheckoutModalOpen && !isPaymentInFlight && !!instanceId,
    check: checkPendingPaymentInBackground,
    onResult: (record, { outcome, cartId, error }) => {
      logActivity('payment-recovery', cartId || record.id, outcome, false, {
        pending_payment_id: record.id,
        method: record.method,
        started_at: record.started_at,
        age_ms: Date.now() - Date.parse(record.started_at),
        cart_id_source: record.cart_id_source,
        ...(error ? { error: error?.message || String(error) } : {}),
      });
    },
  });

  const currency = style?.global?.prices?.currency || 'CLP';
  const locale = style?.global?.prices?.locale || 'es-CL';
  const categoriesPosition = style?.global?.categories?.position || 'top';
  const mainCategoryParams = style?.global?.categories?.mainCategory || {};

  const { filteredCategories, showAllCategory } = useMemo(() => {
    if (!validCategories) return { filteredCategories: filteredCategoriesRef.current, showAllCategory: false };
    let result = [];
    let showAll = false;
    if (selectedCategoryData) {
      if (selectedCategoryData?.children?.length > 0) {
        const children = validCategories.filter((category) => selectedCategoryData.children.includes(category.id));
        children.forEach((child) => {
          let included = false;
          for (let i = 0; i < validProducts.length; i++) {
            const product = validProducts[i];
            const productCategories = product.categories || [];
            included = productCategories.includes(child.id);
            if (included) break;
          }
          if (included) result.push(child);
        });
        if (result.length === 0) {
          result = filteredCategoriesRef.current;
          const deepLevel = selectedCategoryData.deep_level || 0;
          if (deepLevel === 0) showAll = mainCategoryParams?.is_visible || false;
        }
      } else {
        result = filteredCategoriesRef.current;
        const deepLevel = selectedCategoryData.deep_level || 0;
        if (deepLevel === 0) showAll = mainCategoryParams?.is_visible || false;
      }
    } else {
      const involvedCategories = filteredCatalog?.categories || [];
      result = validCategories.filter(c => !c.is_empty && involvedCategories.includes(c.id));
      showAll = mainCategoryParams?.is_visible || false;
    }
    if (result !== filteredCategoriesRef.current) filteredCategoriesRef.current = result;
    return { filteredCategories: result, showAllCategory: showAll };
  }, [filteredCatalog, validCategories, selectedCategoryData, validProducts, mainCategoryParams]);

  const defaultCategory = useMemo(() => {
    if (mainCategoryParams?.is_visible !== false) return { id: 'todos', name: 'Todos', deep_level: 0 };
    if (!filteredCategories || filteredCategories.length === 0) return { id: 'todos', name: 'Todos', deep_level: 0 };
    const sortedCategories = [...filteredCategories].sort((c1, c2) => {
      const order1 = c1.order ? c1.order : Number.MAX_SAFE_INTEGER;
      const order2 = c2.order ? c2.order : Number.MAX_SAFE_INTEGER;
      return order1 - order2;
    });
    const validCats = sortedCategories.filter((category) => {
      return category.is_visible !== false && !category.is_empty && validProducts.some((product) => product.categories.includes(category.id));
    });
    return validCats.length > 0 ? validCats[0] : sortedCategories[0];
  }, [mainCategoryParams?.is_visible, filteredCategories, validProducts]);

  const showHeaderThemeChanger = style?.global?.header?.showThemeChanger || false;
  const showHeaderSearchView = style?.global?.header?.showSearchView || false;

  useEffect(() => {
    if (selectedCategory === null && filteredCategories && filteredCategories.length > 0) setSelectedCategory(showAllCategory ? defaultCategory.id : null);
  }, [selectedCategory, filteredCategories, defaultCategory]);

  // Una categoría puede cerrar mientras el kiosko está parado en ella; se suelta la selección para
  // no dejar al cliente navegando productos de una categoría que acaba de salir de horario.
  useEffect(() => {
    if (selectedCategory && closedCategoryIds.has(selectedCategory)) setSelectedCategory(null);
  }, [selectedCategory, closedCategoryIds]);

  useEffect(() => {
    if (filteredCategories && filteredCategories.length > 0) preloadCategoryImages(filteredCategories);
  }, [filteredCategories]);

  useEffect(() => {
    const showAll = mainCategoryParams?.is_visible;
    const allSelected = selectedCategory === 'todos';
    const nullSelected = selectedCategory === null;
    if (showAll && nullSelected) setSelectedCategory('todos');
    else if (!showAll && allSelected) setSelectedCategory(null);
  }, [mainCategoryParams]);

  const filteredProducts = useMemo(() => {
    if (!validProducts) return [];
    let results = [];
    if (selectedCategory) {
      if (selectedCategory === 'todos') {
        results = validProducts.filter(item => {
          return item.categories.some(category => filteredCategories.map(c => c.id).includes(category));
        });
      } else {
        results = validProducts.filter(item => {
          return item.categories.includes(selectedCategory);
        });
      }
      let featuredProducts = results.filter(product => product.is_featured);
      featuredProducts = featuredProducts.sort((p1, p2) => {
        const p1Order = p1.order || Number.MAX_SAFE_INTEGER;
        const p2Order = p2.order || Number.MAX_SAFE_INTEGER;
        return p1Order - p2Order;
      });
      let normalProducts = results.filter(product => !product.is_featured);
      normalProducts = normalProducts.sort((p1, p2) => {
        const p1Order = p1.order || Number.MAX_SAFE_INTEGER;
        const p2Order = p2.order || Number.MAX_SAFE_INTEGER;
        return p1Order - p2Order;
      });
      return featuredProducts.concat(normalProducts);
    } else {
      results = validProducts.filter(product => product.is_featured);
      if ((!results || results.length === 0) && (validCategories && validCategories.length > 0)) {
        const productsPerLeaf = [];
        let leafs = validCategories.filter(category => !category.children || category.children.length === 0);
        leafs.forEach(leaf => {
          const leafProducts = validProducts.filter(product => product.categories.includes(leaf.id));
          if (leafProducts && leafProducts.length > 0) productsPerLeaf.push({ leaf: leaf, products: leafProducts });
        }); 
        const numberOfProductsPerLeaf = productsPerLeaf.length > 0 ? Math.floor(MAX_MIX_QUANTITY / productsPerLeaf.length) : 0;
        productsPerLeaf.forEach(group => {
          const sortedProducts = group.products.sort((p1, p2) => {
            const p1Order = p1.order || Number.MAX_SAFE_INTEGER;
            const p2Order = p2.order || Number.MAX_SAFE_INTEGER;
            return p1Order - p2Order;
          });
          const selectedProducts = sortedProducts.slice(0, numberOfProductsPerLeaf);
          results = results.concat(selectedProducts);
        });
      } else {
        results = results.sort((p1, p2) => {
          const p1Order = p1.order || Number.MAX_SAFE_INTEGER;
          const p2Order = p2.order || Number.MAX_SAFE_INTEGER;
          return p1Order - p2Order;
        });
      }
      return results;
    }
  }, [validProducts, selectedCategory, filteredCategories, filteredStoreIds, mainCategoryParams]);

  const playlistItemsArray = useMemo(() => {
    if (!validProducts) return [];
    if (!playlist || !playlist?.items) return [];
    const list = [];
    const playlistItems = playlist.items.sort((a, b) => a.priority - b.priority);
    playlistItems.forEach((item) => {
      if (item.type === 'promo-group') {
        if (carousels) {
          let match = null;
          Object.keys(carousels).forEach((key) => {
            const carousel = carousels[key];
            if (carousel.id === item.id) match = carousel;
          });
          if (match) {
            let groupList = [];
            match.products.forEach((productId) => {
              const productsInList = groupList.filter(i => i.type === 'product');
              const productMatch = productsInList.find(p => p.id === productId);
              if (!productMatch) {
                const product = validProducts.find(i => i.id === productId);
                if (product) groupList.push({ id: item.id, item: product, type: 'product', playlistId: playlist.id, playlistName: playlist.name, order: product.order || null });
              }
            });
            groupList = groupList.sort((a, b) => {
              const aOrder = a.item.order || Number.MAX_SAFE_INTEGER;
              const bOrder = b.item.order || Number.MAX_SAFE_INTEGER;
              return aOrder - bOrder;
            });
            groupList.forEach((item) => {
              list.push(item);
            });
          }
        }
      } else if (item.type === 'video') {
        let match = null;
        if (videos) {
          Object.keys(videos).forEach((key) => {
            const video = videos[key];
            if (video.id === item.id) match = video;
          });
          if (match) list.push({ id: item.id, item: match, type: 'video', playlistId: playlist.id, playlistName: playlist.name });
        }
      } else if (item.type === 'image') {
        let match = null;
        if (images) {
          Object.keys(images).forEach((key) => {
            const image = images[key];
            if (image.id === item.id) match = image;
          });
          if (match) list.push({ id: item.id, item: match, type: 'image', playlistId: playlist.id, playlistName: playlist.name });
        }
      }
    });
    return list;
  }, [validProducts, carousels, videos, images, playlist]);

  const validBanners = useMemo(() => {
    if (!banners) return [];
    let possibleBanners = [];
    if (selectedCategory) {
      if (selectedCategory === 'todos') possibleBanners = banners;
      else possibleBanners = banners.filter(banner => banner.categories && banner.categories.includes(selectedCategory));
    } else possibleBanners = banners.filter(banner => !banner.categories || banner.categories.length === 0);
    const validBanners = [];
    const catalogBanners = possibleBanners.filter(banner => banner.catalogs && banner.catalogs.includes(catalogId));
    catalogBanners.forEach((banner) => {
      if (banner.link_type === 'item') {
        const product = validProducts.find(p => p.id === banner.link_target);
        if (product) validBanners.push(banner);
      } else validBanners.push(banner);
    });
    return validBanners;
  }, [validProducts, banners, catalogId, selectedCategory]);

  useEffect(() => {
    if (validBanners && validBanners.length > 0) preloadBannerImages(validBanners);
  }, [validBanners]);

  const cartCount = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartItems]);

  // Items del carro cuyo producto ya no está disponible. No se quitan solos: se marcan, para que
  // el cliente los saque a mano. Quitarlos de la nada se lee como un error de la aplicación, sobre
  // todo si nadie alcanzó a ver la notificación.
  const unavailableCartItemIds = useMemo(() => {
    if (!cartItems || cartItems.length === 0) return new Set();
    return new Set(
      cartItems
        .filter((item) => !validProducts.some((product) => product.id === item.productId))
        .map((item) => item.id)
    );
  }, [cartItems, validProducts]);

  // Un item ya avisado no vuelve a notificar mientras siga sin stock; si reaparece, sale del
  // registro y podrá avisar de nuevo cuando corresponda.
  const notifiedUnavailableIds = useRef(new Set());

  useEffect(() => {
    // Congelado durante el cobro: entre "confirmar pedido" y el cierre del proceso nada toca el
    // carro ni interrumpe al cliente. La validación se retoma sola al salir del pago, que es
    // cuando aparece el botón de reintentar.
    if (isPaymentInFlight) {
      return;
    }

    if (selectedProduct && isModalOpen) {
      const product = getSelectedProductData(selectedProduct);
      if (!product) {
        const selected = getProductById(selectedProduct);
        setIsModalOpen(false);
        setSelectedProduct(null);
        const id = new Date().getTime();
        addNotification(id, 'error', `El producto <b>${selected?.label || selected?.name || selectedProduct}</b> ya no está disponible.`, null, null);
        setTimeout(() => {removeNotification(id)}, NOTIFICATION_DURATION * 1000);
      }
    }

    cartItems.forEach(item => {
      if (!unavailableCartItemIds.has(item.id)) return;
      if (notifiedUnavailableIds.current.has(item.id)) return;
      const product = getProductById(item.productId);
      const id = new Date().getTime();
      addNotification(id, 'error', `El producto <b>${product?.label || product?.name || item.productId}</b> ya no está disponible. Quítalo del carro para continuar.`, null, null);
      setTimeout(() => {removeNotification(id)}, NOTIFICATION_DURATION * 1000);
    });

    notifiedUnavailableIds.current = new Set(unavailableCartItemIds);
  }, [validProducts, cartItems, unavailableCartItemIds, isPaymentInFlight]);

  // Se marca al cambiar la configuración sin recargar la página (ver handleResetConfig).
  const configWasReset = useRef(false);

  const saveConfig = (newConfig) => {
    localStorage.setItem("kiosk_config", JSON.stringify(newConfig));
    const backup = backupConfig(newConfig);
    // Tras un cambio de configuración quedan en memoria el carro, la vista y los datos del cliente
    // anterior; recargar arranca el tótem limpio con la nueva configuración. La animación final
    // del asistente cubre esa espera. Se espera el respaldo para no recargar con el anterior.
    if (configWasReset.current) {
      backup.finally(() => window.location.reload());
      return;
    }
    setConfig(newConfig);
  };

  // Cierra todas las sesiones activas de la instancia (no solo la propia, por si quedó alguna
  // huérfana), deteniendo antes el ping para que no la vuelva a marcar activa.
  const closeCurrentSession = () => {
    stopPingTimer();
    return closeSession();
  };

  // `pendingTask` (opcional) se espera en paralelo con el cierre de la sesión antes de recargar.
  // Vuelve al asistente de inmediato, sin recargar. El cierre de la sesión (sin esto el asistente
  // bloquearía la misma instancia) y `pendingTask` terminan en segundo plano; la recarga que
  // limpia el estado en memoria ocurre al terminar el asistente (ver saveConfig). Solo se borran
  // las claves de la configuración: tema, debug y la fecha de actualización son del equipo.
  const handleResetConfig = (pendingTask = null) => {
    closeCurrentSession();
    Promise.resolve(pendingTask).catch((error) => console.error('Error in config reset task:', error));
    localStorage.removeItem("kiosk_config");
    localStorage.removeItem("kiosk_cart_id");
    // Sin esto, el respaldo devolvería la configuración anterior al recargar.
    clearConfigBackup();
    configWasReset.current = true;
    setSessionId(null);
    setCurrentView('home');
    setConfig(null);
  };

  const handleProductClick = (product) => {
    scannedProductIdRef.current = null;
    setSelectedProduct(product.id);
    setIsModalOpen(true);
  };

  const getSelectedProductData = (productId) => {
    const product = validProducts.find(p => p.id === productId);
    if (!product) return null;
    return product;
  };

  const getSelectedProductModifiers = (productId) => {
    const product = getSelectedProductData(productId);
    const modifiersFound = (product && product.modifiers && product.modifiers.length > 0) ? validModifiers.filter((modifier) => product.modifiers.includes(modifier.id)) : [];
    return modifiersFound;
  };

  const getExtras = (product, productModifiers, selectedExtras) => {
    let extras = [];
    // Se ordena igual que en ProductDetailModal para que el `groupIndex` de las llaves coincida
    sortModifierGroups(productModifiers).forEach((extraGroup, groupIndex) => {
      sortModifierOptions(extraGroup.options).forEach(option => {
        const value = resolveExtraValue(selectedExtras, extraGroup, option, groupIndex);
        if (value === true) {
          extras.push(
            {
              id: option.id,
              sku: option.sku,
              name: option.name,
              quantity: 1,
              price: option.price
            },
          );
        } else if (typeof value === 'number' && value > 0) {
          extras.push(
            {
              id: option.id,
              sku: option.sku,
              name: option.name,
              quantity: value,
              price: option.price
            },
          );
        }
      });
    });
    return extras;
  };

  const getProductModifiers = (modifierIds) => {
    return validModifiers.filter(m => modifierIds.includes(m.id))
  };

  const getProductById = (productId) => {
    return items.find(item => item.id === productId);
  };

  const getValidProductById = (productId) => {
    return validProducts.find(item => item.id === productId);
  };

  const loyaltyContext = useMemo(() => ({
    identifiedPrograms,
    programsById: Object.fromEntries((loyaltyPrograms || []).map((p) => [p.id, p])),
  }), [identifiedPrograms, loyaltyPrograms]);

  const { items: enrichedItems, totalPromoDiscount } = useMemo(() => {
    return applyPromotions(cartItems, promotions || [], items, validModifiers, filteredStoreIds, filteredZones, storeId, loyaltyContext);
  }, [cartItems, promotions, items, validModifiers, filteredStoreIds, filteredZones, storeId, loyaltyContext]);

  const { normalTotal, commonTotal } = cart ? getTotals(enrichedItems, getProductById, getProductModifiers, getProductInfo, getExtras, filteredStoreIds) : { normalTotal: 0, commonTotal: 0 };

  const getCategoryById = (categoryId) => {
    return validCategories.find(category => category.id === categoryId);
  };

  const openProductById = (productId) => {
    const product = getProductById(productId);
    const valid = validProducts.find(p => p.id === productId);
    if (product && valid) handleProductClick(product);
  };

  const addNotification = (id, type, message, undo, undoId) => {
    setNotifications((prev) => [...prev, { id, message, type, undo, undoId }]);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter(notification => notification.id !== id));
  };

  const handleAddToCart = (product, quantity, selectedExtras) => {
    if (!storeHours.open) {
      notifyStoreClosed();
      return;
    }
    const newItem = {
      id: `${product.id}-${Date.now()}`,
      productId: product.id,
      quantity,
      selectedExtras,
    };
    addOrUpdateItem(newItem);
    const id = new Date().getTime();
    addNotification(id, 'success', `Agregado <b>${product.name}</b> x (${quantity})`, 'remove', newItem.id);
    setTimeout(() => {removeNotification(id)}, NOTIFICATION_DURATION * 1000);
  };

  // Un producto escaneado termina en el carro también cuando antes hubo que elegir sus modificadores.
  const handleAddToCartFromProductModal = (product, quantity, selectedExtras) => {
    handleAddToCart(product, quantity, selectedExtras);
    if (scannedProductIdRef.current === product.id) handleCartClick();
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    const current = cartItems.find((item) => item.id === itemId);
    if (!storeHours.open && current && newQuantity > current.quantity) {
      notifyStoreClosed();
      return;
    }
    updateQuantity(itemId, newQuantity);
  };

  const handleUpdateItem = (itemId, newQuantity, newSelectedExtras) => {
    updateItem(itemId, { quantity: newQuantity, selectedExtras: newSelectedExtras });
  };

  const handleUndoAction = (id) => {
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;
    const undo = notification.undo;
    const undoId = notification.undoId;
    switch (undo) {
      case 'remove':
        handleRemoveItem(undoId);
        break;
      default:
        break;
    }
    removeNotification(id);
  };

  const handleRemoveItem = (itemId) => {
    removeItem(itemId);
  };

  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      setIsCrossSellModalOpen(false);
      setIsCheckoutModalOpen(false);
    }
  }, [cartItems]);

  const handleClearCart = async () => {
    // SOK-09: el modo accesible dura una sesión de compra; se apaga al terminar la venta, al vaciar
    // el carro y por inactividad, que pasan todos por aquí.
    if (a11yActive) {
      logActivity('a11y', 'session', 'reset', false, { settings: { ...a11ySettings } });
      resetA11y();
    }
    await newCart();
    createSession();
    resetLoyalty();
  };

  const handleCheckout = async () => {
    try {
      setIsCheckoutModalOpen(true);
    } catch (error) {
      console.error('Checkout Error:', error);
    }
  };

  const handleCategoryClick = (categoryId) => {
    let category = validCategories.find(c => c.id === categoryId);
    if (!category) category = defaultCategory;
    setCurrentView('catalog');
    setSelectedCategory(category.id);
    setBreadcrumbs((previous) => {
      let newBreadcrumbs = [...previous];
      const index = newBreadcrumbs.findIndex(breadcrumb => breadcrumb.deep_level === category.deep_level);
      if (index === -1) newBreadcrumbs.push(category);
      else newBreadcrumbs[index] = category;
      return newBreadcrumbs;
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const buildInitialBreadcrumbs = () => {
    setBreadcrumbs([defaultCategory]);
  };

  useEffect(() => {
    if (selectedCategory === defaultCategory.id) buildInitialBreadcrumbs();
  }, [selectedCategory, defaultCategory]);

  const handleBreadcrumbClick = (category) => {
    setCurrentView('catalog');
    if (category.id === '__start__') {
      setSelectedCategory(showAllCategory ? defaultCategory.id : null);
      setBreadcrumbs([]);
    } else {
      setSelectedCategory(category.id);
      const index = breadcrumbs.findIndex(breadcrumb => breadcrumb.id === category.id);
      if (index !== -1) {
        setBreadcrumbs((previous) => {
          let newBreadcrumbs = [...previous];
          newBreadcrumbs = newBreadcrumbs.slice(0, index);
          newBreadcrumbs.push(category);
          return newBreadcrumbs;
        });
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleNavigateToProducts = () => {
    setCurrentView('catalog');
    setSelectedCategory(showAllCategory ? defaultCategory.id : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHomeClick = () => {
    setCurrentView('home');
    setSelectedCategory(showAllCategory ? defaultCategory.id : null);
    setAllowCrossSellModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchClick = () => {
    setCurrentView('search');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCartProducts = () => {
    const products = [];
    cartItems.forEach(item => {
      const match = getValidProductById(item.productId);
      if (match) products.push(match);
    });
    return products;
  };

  const getCrossSellProducts = () => {
    const crossSellProducts = [];
    getCartProducts().forEach(cartProduct => {
      const crossSells = cartProduct.cross_sell || [];
      crossSells.forEach(crossSell => {
        const match = getValidProductById(crossSell.product_id);
        if (match) crossSellProducts.push(match);
      });
    });
    return crossSellProducts;
  };

  const handleCartClick = () => {
    const crossSellProducts = getCrossSellProducts();
    // SOK-37: con el local cerrado no se ofrece agregar más.
    const showCrossSellModal = storeHours.open && allowCrossSellModal && cartItems && cartItems.length > 0 && crossSellProducts && crossSellProducts.length > 0;
    if (showCrossSellModal) {
      // Exposición del cross-sell: denominador de la tasa de aceptación (SOK-06)
      logActivity('cross-sell', 'modal', 'shown', false, {product_ids: getCrossSellShownIds(crossSellProducts)});
      setIsCrossSellModalOpen(true);
    } else handleOpenCart();
  };

  const handleOpenCart = () => {
    setCurrentView('cart');
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const handleBackFromCart = () => {
    setCurrentView('catalog');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackFromSearch = () => {
    setCurrentView('catalog');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    if (clickSequence === 0) {
      setClickSequence(1);
    } else if (clickSequence === 3) {
      setClickSequence(0);
      handleOpenSecretMenu();
    } else {
      setClickSequence(0);
    }
  };

  const handleVersionTagClick = () => {
    if (clickSequence === 1) {
      setClickSequence(2);
    } else if (clickSequence === 2) {
      setClickSequence(3);
    } else {
      setClickSequence(0);
    }
  };

  const handleOpenSecretMenu = () => {
    logActivity('secret-menu', 'open-sequence', 'apply', true, null);
    setIsSecretMenuModalOpen(true);
  };

  const handleCloseSecretMenu = () => {
    setIsSecretMenuModalOpen(false);
    setClickSequence(0);
  };

  const handleGoToCashClosing = () => {
    setIsSecretMenuModalOpen(false);
    setClickSequence(0);
    setCurrentView('cash_closing');
  };

  const addPaymentError = (error) => {
    sessionHealthRef.current.lastError = { at: serverNow(), type: error.type, message: error.message };
    setPaymentErrors((prev) => [...prev, error]);
  };

  // Guarda en memoria la última verificación del POS; se envía en el siguiente ping de la sesión.
  const recordPOSCheck = (provider, ok, checkedBy, error) => {
    sessionHealthRef.current.paymentTerminal = toPaymentTerminal({ provider, ok, checkedBy, at: serverNow(), error });
  };

  const handlePOSPaymentResult = ({ provider, paid, communicationError }) => {
    const check = posCheckFromPayment({ paid, communicationError });
    if (provider && check) recordPOSCheck(provider, check.ok, POS_CHECKED_BY.PAYMENT, check.error);
  };

  // Mismo poll que pollingViaAPI, pero deja el resultado como última verificación del POS. El POS
  // atiende una tarea a la vez y un poll en paralelo a un cobro puede bloquearlo, así que mientras
  // corre la pantalla queda bloqueada (con un límite de tiempo). Si quien lo pidió lo cancela, no
  // dice nada del equipo y no se guarda. `automatic` marca los que el tótem hace solo: el backend no
  // imprime nada en el terminal (los manuales de Mercado Pago imprimen un comprobante de prueba).
  const pollPOSAndRecord = async (signal, type, automatic = false) => {
    if (posPollInFlightRef.current) return { status: 'error' };
    posPollInFlightRef.current = true;
    setIsAutomaticPOSPoll(automatic);
    setIsPollingPOS(true);
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal?.addEventListener('abort', cancel);
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, POS_POLL_TIMEOUT_MS);
    try {
      const response = await pollingViaAPI(controller.signal, type, automatic);
      if (timedOut) recordPOSCheck(type, false, POS_CHECKED_BY.POLL, POS_POLL_TIMEOUT_ERROR);
      else if (!signal?.aborted) recordPOSCheck(type, response?.status === 'OK', POS_CHECKED_BY.POLL, null);
      return response;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
      posPollInFlightRef.current = false;
      setIsPollingPOS(false);
    }
  };

  // Con el tótem inactivo en la pantalla inicial, prueba el POS cada 5 min. Con uso alto no hace
  // falta: los pagos mantienen el dato al día y cuentan como verificación.
  const pollPOSOnIdle = () => {
    const provider = getPOSProvider(style?.global?.paymentMethods);
    const busy = isPaymentInFlight || isCheckoutModalOpen || isIdentifyModalOpen || isModalOpen || isSecretMenuModalOpen || isConnectingPOS || isScanning || posPollInFlightRef.current;
    const lastCheck = sessionHealthRef.current.paymentTerminal?.last_check || null;
    const nowMs = serverNow().getTime();
    const lastInteractionMs = lastInteractionRef.current + (nowMs - Date.now());
    if (!shouldPollPOSOnIdle({ provider, lastCheck, nowMs, lastInteractionMs, screen: currentView, busy })) return;
    pollPOSAndRecord(null, provider, true);
  };

  const clearPaymentErrors = () => {
    setPaymentErrors([]);
  };

  useEffect(() => {
    if (paymentErrors && paymentErrors.length >= PAYMENT_ERRORS_THRESHOLD) {
      const _paymentErrors = [...paymentErrors];
      const adminEmails = [...new Set(admins.map(a => a.email))];
      clearPaymentErrors();
      Email.sendPaymentErrorsEmail(clientData.name || client, instance.id, adminEmails, _paymentErrors);
    }
  }, [paymentErrors]);

  useEffect(() => {
    if (style) {
      const primaryActionStyle = style?.global?.actionButton;
      const pricesStyle = style?.global?.prices;
      const menuActionStyle = style?.global?.actionBar?.menu;
      const scanActionStyle = style?.global?.actionBar?.scan;
      const categoryButtonStyle = style?.global?.categories;
      const tagsStyle = style?.global?.tags;
      const fontsStyle = style?.global?.fonts;
      const checkoutConfirmButtonStyle = style?.global?.checkout?.confirmButton;
      if (primaryActionStyle) {
        if (primaryActionStyle.backgroundColor) document.documentElement.style.setProperty("--color-primary", primaryActionStyle.backgroundColor);
        if (primaryActionStyle.borderColor) document.documentElement.style.setProperty("--color-primary-border", primaryActionStyle.borderColor);
        if (primaryActionStyle.labelColor) document.documentElement.style.setProperty("--color-primary-label", primaryActionStyle.labelColor);
        if (primaryActionStyle.glowColor) document.documentElement.style.setProperty("--color-primary-glow", primaryActionStyle.glowColor);
      }
      if (pricesStyle) {
        if (pricesStyle.commonPriceLabelColor) document.documentElement.style.setProperty("--color-common-price", pricesStyle.commonPriceLabelColor);
      }
      if (menuActionStyle) {
        if (menuActionStyle.backgroundColor) document.documentElement.style.setProperty("--color-action-menu", menuActionStyle.backgroundColor);
        if (menuActionStyle.borderColor) document.documentElement.style.setProperty("--color-action-menu-border", menuActionStyle.borderColor);
        if (menuActionStyle.labelColor) document.documentElement.style.setProperty("--color-action-menu-label", menuActionStyle.labelColor);
        if (menuActionStyle.glowColor) document.documentElement.style.setProperty("--color-action-menu-glow", menuActionStyle.glowColor);
      }
      if (scanActionStyle) {
        if (scanActionStyle.backgroundColor) document.documentElement.style.setProperty("--color-action-scan", scanActionStyle.backgroundColor);
        if (scanActionStyle.borderColor) document.documentElement.style.setProperty("--color-action-scan-border", scanActionStyle.borderColor);
        if (scanActionStyle.labelColor) document.documentElement.style.setProperty("--color-action-scan-label", scanActionStyle.labelColor);
      }
      if (categoryButtonStyle) {
        if (categoryButtonStyle.borderColor) document.documentElement.style.setProperty("--cat-border", categoryButtonStyle.borderColor);
        if (categoryButtonStyle.backgroundColor) document.documentElement.style.setProperty("--cat-background", categoryButtonStyle.backgroundColor);
      }
      if (tagsStyle) {
        if (tagsStyle.backgroundColor) document.documentElement.style.setProperty("--tag-bg", tagsStyle.backgroundColor);
        if (tagsStyle.color) document.documentElement.style.setProperty("--tag-color", tagsStyle.color);
      }
      if (checkoutConfirmButtonStyle) {
        if (checkoutConfirmButtonStyle.backgroundColor) document.documentElement.style.setProperty("--checkout-confirm-bg", checkoutConfirmButtonStyle.backgroundColor);
        if (checkoutConfirmButtonStyle.labelColor) document.documentElement.style.setProperty("--checkout-confirm-color", checkoutConfirmButtonStyle.labelColor);
        if (checkoutConfirmButtonStyle.glowColor) document.documentElement.style.setProperty("--checkout-confirm-glow", checkoutConfirmButtonStyle.glowColor);
      }
      if (fontsStyle?.primary && fontsStyle?.primary?.family && fontsStyle?.primary?.url) setCustomFont(fontsStyle?.primary?.family, fontsStyle?.primary?.url, 'main-font');
      else document.documentElement.style.setProperty("--main-font", "Averia Serif Libre, sans-serif");
      if (fontsStyle?.secondary && fontsStyle?.secondary?.family && fontsStyle?.secondary?.url) setCustomFont(fontsStyle?.secondary?.family, fontsStyle?.secondary?.url, 'secondary-font');
      else document.documentElement.style.setProperty("--secondary-font", "Sansation, sans-serif");
      const backendTheme = style?.styles?.theme || null;
      const storageTheme = localStorage.getItem("kiosk_theme") || null;
      if (!showHeaderThemeChanger && backendTheme && backendTheme !== storageTheme) setTheme(backendTheme);
    }
  }, [style]);

  const setCustomFont = async (fontFamily, fontUrl, cssVariable) => {
    const customFont = new FontFace(fontFamily, `url(${fontUrl})`);
    const loadedFont = await customFont.load();
    document.fonts.add(loadedFont);
    document.documentElement.style.setProperty(`--${cssVariable}`, loadedFont.family);
  };

  if (cartLoading && !cart) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">Cargando...</p>
      </div>
    );
  };

  const getDimensions = () => {
    const width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const height = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    return {
      width: width,
      height: height
    };
  };

  const dimensions = getDimensions();

  const renderUpdateAlert = () => {
    return (
      <UpdateAlert show={showUpdateAlert} reloadApp={reloadApp} onClose={closeUpdateAlert} />
    );
  };

  return (
    <div>
      {renderUpdateAlert()}
      {isLoadingData || isRestoringConfig ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className='spin mr-1'>
              <LoaderCircle width={30} height={30} />
            </div>
            <p className="w-full text-center text-lg secondary-font-bold">Cargando datos...</p>
          </div>
        </div>
      ) : (
        <>
          {!config ? <SetupWizard env={env} onConfigFinished={saveConfig} /> : null}
          {config && instance && (instance.disabled || !hasSelfOrderingModule) ? <UnavailableMessage /> : null}
          {config && instance && !instance.disabled && hasSelfOrderingModule ? (
            <>
            <LowZoneNotice />
            {/* SOK-02: durante el cobro (cuenta regresiva y "Revisando tu pago") la pantalla manda al
                cliente al POS; el botón, ni grande ni en pestaña, compite con eso. Vuelve al terminar. */}
            {!isPaymentInFlight && !showStoreClosed ? <AccessibilityButton alwaysVisible={currentView === 'home'} logActivity={logActivity} /> : null}
            {showStoreClosingBar ? (
              <StoreClosingBar
                closesAt={storeHours.closesAt}
                closed={!storeHours.open}
                timeZone={storeHours.timeZone}
              />
            ) : null}
            <div className="a11y-viewport">
            <div ref={rootRef} className="flex flex-col h-(--app-h) overflow-y-auto">
              {!showStoreClosed ? (
                <div className='app-version-tag'>
                  <VersionTag version={appData.version} connectivity={connectivity} onClick={handleVersionTagClick} />
                </div>
              ) : null}

              {createPortal(
                <AnimatePresence>
                  {notifications.map((notification) => (
                    <Notification
                      key={notification.id}
                      id={notification.id}
                      type={notification.type}
                      message={notification.message}
                      allowUndo={!isCheckoutModalOpen}
                      undo={notification.undo}
                      onUndo={handleUndoAction}
                    />
                  ))}
                </AnimatePresence>,
                document.getElementById('notification-root')
              )}

              <ScanOverlay open={isScanning} />
              <ScanOverlay open={isPOSPollBlocking} message="Probando conexión con el terminal de pago..." />

              {showStoreClosed ? (
                <StoreClosedScreen
                  status={storeHours}
                  style={style}
                  version={appData.version}
                  connectivity={connectivity}
                  onLogoClick={handleLogoClick}
                  onVersionClick={handleVersionTagClick}
                />
              ) : currentView === "home" ? (
                <Home
                  currency={currency}
                  locale={locale}
                  showHeaderThemeChanger={showHeaderThemeChanger}
                  showHeaderSearchView={showHeaderSearchView}
                  onNavigateToProducts={handleNavigateToProducts}
                  cartItemsCount={cartCount}
                  onCartClick={handleCartClick}
                  openProductById={openProductById}
                  openCategoryById={handleCategoryClick}
                  onLogoClick={handleLogoClick}
                  onSearchClick={handleSearchClick}
                  playlist={playlist}
                  playlistItems={playlistItemsArray}
                  style={style}
                  filteredStoreIds={filteredStoreIds}
                  logActivity={logActivity}
                />
              ) : currentView === 'cart' ? (
                <ShoppingCart
                  currency={currency}
                  locale={locale}
                  cart={cart}
                  cartItems={cartItems}
                  unavailableItemIds={unavailableCartItemIds}
                  commonTotal={commonTotal}
                  normalTotal={normalTotal}
                  enrichedItems={enrichedItems}
                  totalPromoDiscount={totalPromoDiscount}
                  sellers={sellers}
                  sellerColors={sellerColors}
                  onUpdateQuantity={handleUpdateQuantity}
                  onUpdateItem={handleUpdateItem}
                  onRemoveItem={handleRemoveItem}
                  onClearCart={handleClearCart}
                  onBack={handleBackFromCart}
                  onCheckout={handleCheckout}
                  getExtras={getExtras}
                  isSearching={isScanning}
                  style={style}
                  filteredStoreIds={filteredStoreIds}
                  storeId={storeId}
                  getProductById={getProductById}
                  getProductModifiers={getProductModifiers}
                  validProducts={validProducts}
                  drawerContainer={rootRef.current}
                  logActivity={logActivity}
                  loyaltyPrograms={loyaltyPrograms}
                  identifiedPrograms={identifiedPrograms}
                  onOpenIdentification={() => setIsIdentifyModalOpen(true)}
                />
              ) : currentView === 'search' ? (
                <SearchView
                  currency={currency}
                  locale={locale}
                  showHeaderThemeChanger={showHeaderThemeChanger}
                  showHeaderSearchView={showHeaderSearchView}
                  products={validProducts}
                  cart={cart}
                  cartItems={cartItems}
                  total={commonTotal}
                  sellers={sellers}
                  sellerColors={sellerColors}
                  onCancel={handleBackFromSearch}
                  onProductClick={handleProductClick}
                  cartCount={cartCount}
                  onHomeClick={handleHomeClick}
                  onSearchClick={handleSearchClick}
                  onCartClick={handleCartClick}
                  onLogoClick={handleLogoClick}
                  onCheckout={handleCartClick}
                  logActivity={logActivity}
                  style={style}
                  filteredStoreIds={filteredStoreIds}
                />
              ) : currentView === 'cash_closing' ? (
                <CashClosingView
                  currency={currency}
                  locale={locale}
                  env={env}
                  client={client}
                  instanceId={instanceId}
                  onBack={handleHomeClick}
                  drawerContainer={rootRef.current}
                />
              ) : (
                <>
                  <Header
                    showThemeChanger={showHeaderThemeChanger}
                    showSearchView={showHeaderSearchView}
                    cartCount={cartCount}
                    onHomeClick={handleHomeClick}
                    onSearchClick={handleSearchClick}
                    onCartClick={handleCartClick}
                    onLogoClick={handleLogoClick}
                    logActivity={logActivity}
                    style={style}
                  />
                  <div className={`${categoriesPosition === 'left' ? 'bg-mid-gradient' : ''}`}>
                    <div style={{ paddingLeft: categoriesPosition === 'left' && dimensions.width < 1600 ? 150 : 0 }}>
                      {validBanners && validBanners.length > 0 && currentView !== 'cart' ? (
                        <div className='w-full'>
                          <BannerCarousel
                            banners={validBanners}
                            openProductById={openProductById}
                            openCategoryById={handleCategoryClick}
                            getProductById={getProductById}
                            getCategoryById={getCategoryById}
                            categoriesPosition={categoriesPosition}
                            logActivity={logActivity}
                          />
                        </div>
                      ) : null}
                      {shouldShowCategories && categoriesPosition === 'left' ? (
                        <div className="w-full max-w-screen-xl mx-auto">
                          <Breadcrumbs
                            breadcrumbs={breadcrumbs}
                            showBackground={theme === 'light'}
                            categoriesPosition={categoriesPosition}
                            onClick={handleBreadcrumbClick}
                            logActivity={logActivity}
                          />
                        </div>
                      ) : null}
                      {/* SOK-09: en la franja baja no hay alto para dejar las categorías fijas. */}
                      {shouldShowCategories && categoriesPosition === 'top' && filteredCategories && filteredCategories.length > 0 ? (
                        <div className={dimensions.height > 1000 && !a11ySettings.low ? "sticky top-[110px] z-50" : ""}>
                          <CategoryFilter
                            categories={filteredCategories}
                            selectedCategory={selectedCategory}
                            showAll={showAllCategory}
                            breadcrumbs={breadcrumbs}
                            onCategoryChange={handleCategoryClick}
                            onBreadcrumbClick={handleBreadcrumbClick}
                            position="top"
                            logActivity={logActivity}
                            mainCategoryParams={mainCategoryParams}
                          />
                        </div>
                      ) : null}
                      <div className={`relative flex place-content-between ${categoriesPosition === 'left' && validBanners.length === 0 ? 'bg-mid-gradient' : ''}`}>
                        {shouldShowCategories && categoriesPosition === 'left' && filteredCategories && filteredCategories.length > 0 ? (
                          <div className="fixed h-(--app-h) top-[0px] left-[0px]" style={{ width: 150, height: '100%', flexShrink: 0, paddingTop: 112, backgroundColor: 'var(--cat-background)' }}>
                            <CategoryFilter
                              categories={filteredCategories}
                              selectedCategory={selectedCategory}
                              showAll={showAllCategory}
                              breadcrumbs={[]}
                              onCategoryChange={handleCategoryClick}
                              position="left"
                              logActivity={logActivity}
                              mainCategoryParams={mainCategoryParams}
                            />
                          </div>
                        ) : null}
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={selectedCategory}
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="mx-auto w-full"
                          >
                            <ProductGrid
                              currency={currency}
                              locale={locale}
                              from="catalog"
                              query={null}
                              products={filteredProducts}
                              sellers={sellers}
                              sellerColors={sellerColors}
                              style={style}
                              onProductClick={handleProductClick}
                              filteredStoreIds={filteredStoreIds}
                              categoriesPosition={categoriesPosition}
                              disableFeaturedBorder={!selectedCategory && validProducts.filter(product => product.is_featured).length > 0}
                              logActivity={logActivity}
                            />
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  <FloatingCartNotice
                    currency={currency}
                    locale={locale}
                    cart={cart}
                    cartCount={cartCount}
                    onCheckout={handleCartClick}
                    total={commonTotal}
                    logActivity={logActivity}
                  />
                </>
              )}

              {/* Modal de detalle de producto */}
              <ProductDetailModal
                currency={currency}
                locale={locale}
                storeId={storeId}
                product={getSelectedProductData(selectedProduct)}
                validProducts={validProducts}
                modifiers={getSelectedProductModifiers(selectedProduct)}
                sellers={sellers}
                sellerColors={sellerColors}
                open={isModalOpen}
                onClose={() => { scannedProductIdRef.current = null; setIsModalOpen(false); }}
                onUpsellClick={openProductById}
                onAddToCart={handleAddToCartFromProductModal}
                getProductById={getProductById}
                filteredStoreIds={filteredStoreIds}
                logActivity={logActivity}
                drawerContainer={rootRef.current}
              />

              {/* CrossSell Modal */}
              {allowCrossSellModal ? (
                <CrossSell
                  currency={currency}
                  locale={locale}
                  storeId={storeId}
                  open={isCrossSellModalOpen}
                  cartItems={cartItems}
                  filteredStoreIds={filteredStoreIds}
                  validProducts={validProducts}
                  sellers={sellers}
                  sellerColors={sellerColors}
                  getValidProductById={getValidProductById}
                  getProductById={getProductById}
                  getProductModifiers={getProductModifiers}
                  getCartProducts={getCartProducts}
                  addToCart={handleAddToCart}
                  goToCart={handleOpenCart}
                  drawerContainer={rootRef.current}
                  setAllowCrossSellModal={setAllowCrossSellModal}
                  onClose={() => {
                    setIsCrossSellModalOpen(false);
                    setAllowCrossSellModal(false);
                    handleOpenCart();
                  }}
                  logActivity={logActivity}
                />
              ) : null}

              {/* SecretMenu Modal */}
              <SecretMenu
                clientId={client}
                storeId={storeId}
                instance={instance}
                reloadApp={reloadApp}
                onResetConfig={handleResetConfig}
                onClose={handleCloseSecretMenu}
                onGoToCashClosing={handleGoToCashClosing}
                pollingViaAPI={pollPOSAndRecord}
                isOpen={isSecretMenuModalOpen}
                isConnectingPOS={isConnectingPOS}
                setIsConnectingPOS={setIsConnectingPOS}
                logActivity={logActivity}
                style={style}
                drawerContainer={rootRef.current}
              />

              {/* Modal de checkout */}
              <CheckoutModal
                currency={currency}
                locale={locale}
                env={env}
                client={client}
                open={isCheckoutModalOpen}
                onClose={() => setIsCheckoutModalOpen(false)}
                cartItems={cartItems}
                unavailableItemIds={unavailableCartItemIds}
                onPaymentInFlightChange={setIsPaymentInFlight}
                canStartPayment={canStartPayment(connectivity)}
                resumePendingPayment={resumePendingPayment}
                enrichedItems={enrichedItems}
                commonTotal={commonTotal}
                products={items}
                checkoutAndWaitStatusViaAPI={checkoutAndWaitStatusViaAPI}
                cartId={cartId}
                cart={cart}
                store={currentStore}
                checkOrderStatus={checkOrderStatus}
                onClearCart={handleClearCart}
                setOrderStatus={setOrderStatus}
                setLoading={setLoading}
                addPaymentError={addPaymentError}
                clearPaymentErrors={clearPaymentErrors}
                onPOSPaymentResult={handlePOSPaymentResult}
                clientData={clientData}
                instance={instance}
                currentView={currentView}
                style={style}
                backToCatalog={() => {
                  setAllowCrossSellModal(true);
                  handleBackFromCart();
                }}
                backToHome={handleHomeClick}
                // KIO-03: el modal la llama con (instance, currentView); sin los tiempos, la
                // función detenía el temporizador en vez de reiniciarlo tras un cobro fallido.
                shouldStartInactivityTimer={(instance, currentView) => shouldStartInactivityTimer(instance, currentView, inactivityRedirectTimeout, inactivityDialogTimeout)}
                stopInactivityTimer={stopInactivityTimer}
                remoteClear={checkoutRemoteClear}
                logActivity={logActivity}
                drawerContainer={rootRef.current}
              />

              {/* Modal de identificación (loyalty) */}
              <IdentificationModal
                open={isIdentifyModalOpen}
                onClose={() => setIsIdentifyModalOpen(false)}
                programs={loyaltyPrograms}
                identifiedPrograms={identifiedPrograms}
                onIdentify={identify}
                onLogout={resetLoyalty}
                logActivity={logActivity}
                drawerContainer={rootRef.current}
              />

              {/* Inactivity Modal */}
              <InactivityModal
                showInactivityDialog={showInactivityDialog}
                setShowInactivityDialog={setShowInactivityDialog}
                backToCatalog={handleBackFromCart}
                setCheckoutRemoteClear={setCheckoutRemoteClear}
                setIsSecretMenuModalOpen={setIsSecretMenuModalOpen}
                setIsProductModalOpen={setIsModalOpen}
                handleClearCart={handleClearCart}
                elapsedTime={inactivityCounter}
                redirectTimeout={inactivityRedirectTimeout}
                logActivity={logActivity}
                drawerContainer={rootRef.current}
              />
            </div>
            </div>
            </>
          ) : null}
        </>
      )}

    </div>
  );
}

export default App